import safeGetAttribute from './safeGetAttribute';
import { INLINEIMAGE_ATTRIBUTE_ORIGINALSRC } from 'owa-inline-image-consts';
import { DatapointStatus } from 'owa-analytics';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';
import { format } from 'owa-string-utils';
import loadImageFromSrc from './loadImageFromSrc';
import { isConsumer } from 'owa-session-store';
import { getLogicalRing } from 'owa-config';
import createImageProxyDatapoint, {
    ImageProxyEndpoint,
    ImageProxyTelemetryConfig,
} from './createImageProxyDatapoint';
import { isFeatureEnabled } from 'owa-feature-flags';

// Traffic types
const TRAFFIC_PRIMARY = 'p';
const TRAFFIC_SHADOWING = 's';

// User types
const USERTYPE_CONSUMER = 'c';
const USERTYPE_BUSINESS = 'b';

// Status code
export const STATUS_CODE = 'statusCode';

// Meaning of url params
// u - real image url
// t - auth token
// r - t(r)affic type, p: primary, s: shadowing
// s - u(s)er, c: consumer, b: business
const IMAGE_PROXY_MODEL_D_FORMAT_URL =
    'https://imgproxy-{0}.services.web.outlook.com/proxy/?u={1}&t={2}&r={3}&s={4}';
const IMAGE_PROXY_COSMOS_FORMAT_URL =
    'https://cosmic-{0}.office.net/imageproxy/proxy?u={1}&t={2}&r={3}&s={4}';

// The resource url for model D image proxy.
// TODO: we will need to update it to match with IMAGE_PROXY_URL where it is really served
const IMAGE_PROXY_RESOURCE_URL = 'https://outlookimageproxy.azurewebsites.net';

const MODEL_D_CLIENT_ERROR_REASON = 'clientErrorReason';

const MODEL_D_SERVER_ERROR_PROXYCODE = 'serverErrorProxyCode';

const IS_SHADOW_TRAFFIC = 'isShadowTraffic';

// Is https or not
const IS_HTTPS = 'isHttps';

const enum ModelDClientErrorReason {
    Unknown = 0,
    TokenPromiseRejected = 1,
    TokenLengthIsZero = 2,
    RenderFailed = 3,
    FetchFailed = 4,
}

type RingSet = {
    sdf: string;
    prod: string;
};

type ClusterRingMap = Record<Exclude<ImageProxyEndpoint, ImageProxyEndpoint.ModelB>, RingSet>;
const CLUSTER_RINGS: ClusterRingMap = {
    [ImageProxyEndpoint.Cosmic]: {
        sdf: 'sdf',
        prod: 'msit',
    },
    [ImageProxyEndpoint.ModelD]: {
        sdf: 'sdf',
        prod: 'prod',
    },
};

function getImageProxyRequestUrl(
    endpoint: ImageProxyEndpoint,
    originalSrc: string,
    token: string,
    shadowTraffic: boolean
): string {
    const clientRing = getLogicalRing() == 'Dogfood' ? 'sdf' : 'prod';
    return format(
        endpoint == ImageProxyEndpoint.Cosmic
            ? IMAGE_PROXY_COSMOS_FORMAT_URL
            : IMAGE_PROXY_MODEL_D_FORMAT_URL,
        CLUSTER_RINGS[endpoint][clientRing],
        encodeURIComponent(originalSrc),
        encodeURIComponent(token),
        shadowTraffic ? TRAFFIC_SHADOWING : TRAFFIC_PRIMARY,
        isConsumer() ? USERTYPE_CONSUMER : USERTYPE_BUSINESS
    );
}

export default async function loadFromModelDProxy(
    endpoint: ImageProxyEndpoint,
    img: HTMLImageElement,
    shadowTraffic: boolean,
    telemetryConfig: ImageProxyTelemetryConfig,
    delayRevoke: boolean
): Promise<boolean> {
    // In non-shadowing, load happens on real img. In shadowing, we create a temp un-attached img
    const modelDImage = shadowTraffic ? document.createElement('img') : img;

    const originalSrc = safeGetAttribute(img, INLINEIMAGE_ATTRIBUTE_ORIGINALSRC);
    if (!originalSrc) {
        // Model-D can't work without originalSrc
        return false;
    }

    const modelDDatapoint = createImageProxyDatapoint(
        {
            endpoint,
        },
        telemetryConfig
    );

    modelDDatapoint.addCustomProperty(IS_SHADOW_TRAFFIC, shadowTraffic);
    modelDDatapoint.addCustomProperty(
        IS_HTTPS,
        originalSrc.substring(0, 6).toLowerCase() == 'https:'
    );

    let [token, tokenPromise] = getAccessTokenforResourceAsLazy(IMAGE_PROXY_RESOURCE_URL);
    modelDDatapoint.addCustomProperty('tokenCache', !!token ? 'warm' : 'cold');
    if (!token) {
        try {
            token = (await tokenPromise) as string;
        } catch (e) {
            modelDDatapoint.addCustomProperty(
                MODEL_D_CLIENT_ERROR_REASON,
                ModelDClientErrorReason.TokenPromiseRejected
            );
            modelDDatapoint.endWithError(DatapointStatus.ClientError);
            return false;
        }
    }

    modelDDatapoint.addCheckmark('token_loaded');

    // Only attempt to load when we have a token
    const tokenLength = token ? (token as string).length : 0;
    if (tokenLength > 0) {
        const requestDate = Date.now();
        const proxySrc = getImageProxyRequestUrl(
            endpoint,
            originalSrc,
            token as string,
            shadowTraffic
        );

        try {
            const response = await loadImageFromSrc(modelDImage, proxySrc, delayRevoke);
            modelDDatapoint.addCustomProperty(STATUS_CODE, response.status);
            if (response.renderSuccess) {
                let loadFromBrowserCacheTag = 'unknown';
                const responseDateString = response?.headers?.get('date');
                if (responseDateString) {
                    const responseDate = Date.parse(responseDateString);
                    if (!isNaN(responseDate)) {
                        const loadFromBrowserCache = responseDate - requestDate > -1000;
                        loadFromBrowserCacheTag = loadFromBrowserCache ? 'NoCache' : 'BrowserCache';
                    }
                }

                modelDDatapoint.addCustomProperty('loadImgFrom', loadFromBrowserCacheTag);
                modelDDatapoint.endWithSuccess();
                return true;
            } else if (response.status === 200) {
                modelDDatapoint.addCustomProperty(
                    MODEL_D_CLIENT_ERROR_REASON,
                    ModelDClientErrorReason.RenderFailed
                );
                modelDDatapoint.endWithError(DatapointStatus.ClientError);
            } else {
                modelDDatapoint.addCustomProperty(
                    MODEL_D_SERVER_ERROR_PROXYCODE,
                    isFeatureEnabled('rp-imageProxy-loadViaFetch')
                        ? response?.headers?.get('x-proxy-errorcode') || 'EmptyXProxyErrorCode'
                        : 'NoXProxyCodeAsViaTag'
                );
                modelDDatapoint.endWithError(DatapointStatus.ServerError);
            }
        } catch (e) {
            modelDDatapoint.addCustomProperty(
                MODEL_D_CLIENT_ERROR_REASON,
                ModelDClientErrorReason.FetchFailed
            );
            modelDDatapoint.endWithError(DatapointStatus.ClientError);
        }
    } else {
        modelDDatapoint.addCustomProperty(
            MODEL_D_CLIENT_ERROR_REASON,
            ModelDClientErrorReason.TokenLengthIsZero
        );
        modelDDatapoint.endWithError(DatapointStatus.ClientError);
    }

    return false;
}
